import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { MenuTabs } from '~/components/ui/MenuTabs';
import { PanelControls } from '~/components/ui/PanelControls';
import { SearchInput } from '~/components/ui/SearchInput';
import { ShopCategoriesGrid } from '~/components/ui/Shop';
import {
	ShopCategory,
	useShopCategoryControls,
} from '~/components/ui/ShopCategory';
import { rage, useObservable } from '~/rage';
import { InputKey, TechTuningCategory } from '~/shared';
import {
	setActiveCategory,
	setActiveStage,
	setMenuTabsLocked,
	setSearchValue,
	setTabIndex,
	setTabIndexNext,
	setTabIndexPrev,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { ListTab } from '../ListTab';
import { OpenCaseBanner } from '../OpenCaseBanner';
import { categories } from './CategoryTab.model';
import {
	StyledControlsContainer,
	StyledMarginContainer,
} from './CategoryTab.styled';

const CategoryTab = () => {
	const dispatch = useLayoutDispatch();
	const shopItems = useObservable(rage.techTuning.getShopItems());
	const activeCategory = useLayoutSelector(
		(state) => state.tuningShop.activeCategory,
	);
	const tabs = ['Магазин', 'Инвентарь'];
	const tabIndex = useLayoutSelector((state) => state.tuningShop.tabIndex);
	const isInventory = tabIndex === 1;
	const searchValue = useLayoutSelector(
		(state) => state.tuningShop.searchValue,
	);
	const casesOverlayVisible = useLayoutSelector(
		(state) => state.tuningShop.casesOverlayVisible,
	);
	const caseResultVisible = useLayoutSelector(
		(state) => state.tuningShop.caseResultVisible,
	);

	const visibleCategories = useMemo(
		() => [
			categories[0],
			...categories.filter((category) =>
				shopItems.some((item) => item.category === category.type),
			),
		],
		[shopItems],
	);

	const getActiveCategoryIndex = () => {
		if (!activeCategory) return null;
		const index = visibleCategories.findIndex(
			(cat) => cat.type === activeCategory,
		);
		return index >= 0 ? index : null;
	};

	const handleSelect = useCallback((category: TechTuningCategory) => {
		if (category === TechTuningCategory.SETTINGS) {
			dispatch(setActiveStage(StageTypes.Handling));
			return;
		}

		dispatch(setActiveCategory(category));
		dispatch(setActiveStage(StageTypes.List));
	}, []);

	const handleSearch = useCallback((value: string) => {
		dispatch(setSearchValue(value));
		if (value.trim().length > 0) {
			dispatch(setActiveCategory(null));
			dispatch(setActiveStage(StageTypes.List));
		}
	}, []);

	const { setMaxCategoryIndex, activeCategoryIndex, setActiveCategoryIndex } =
		useShopCategoryControls(
			getActiveCategoryIndex(),
			(index) => {
				handleSelect(visibleCategories[index].type);
			},
			casesOverlayVisible || caseResultVisible || isInventory,
		);

	const controls = useMemo(
		() => [
			{
				text: 'Управление',
				keys: [
					InputKey.ArrowUp,
					InputKey.ArrowDown,
					InputKey.ArrowLeft,
					InputKey.ArrowRight,
				],
			},
			{
				text: 'Выбрать категорию',
				keys: [InputKey.Enter],
				onClick: () =>
					handleSelect(visibleCategories[activeCategoryIndex || 0].type),
			},
		],
		[visibleCategories, activeCategoryIndex, handleSelect],
	);

	useEffect(() => {
		setMaxCategoryIndex(visibleCategories.length);
	}, [tabIndex, visibleCategories.length]);

	useEffect(() => {
		if (isInventory && activeCategory !== null) {
			dispatch(setActiveCategory(null));
		}
	}, [isInventory, activeCategory]);

	return (
		<Fragment>
			<div>
				<MenuTabs
					tabs={tabs}
					activeIndex={tabIndex}
					handlePrev={() => dispatch(setTabIndexPrev())}
					handleNext={() => dispatch(setTabIndexNext())}
					setIndex={(index) => dispatch(setTabIndex(index))}
					style={{ marginTop: 20 }}
				/>
				{isInventory ? (
					<ListTab embedded />
				) : (
					<Fragment>
						<StyledMarginContainer>
							<OpenCaseBanner />
						</StyledMarginContainer>
						<StyledMarginContainer>
							<SearchInput
								placeholder="Введите название для поиска"
								value={searchValue}
								onChange={(e) => handleSearch(e.target.value)}
								active={!!searchValue.trim()}
								onFocus={() => dispatch(setMenuTabsLocked(true))}
								onBlur={() => dispatch(setMenuTabsLocked(false))}
							/>
						</StyledMarginContainer>
						<ShopCategoriesGrid style={{ marginTop: 20 }}>
							{visibleCategories.map((category, index) => (
								<ShopCategory
									name={category.name}
									icon={category.image}
									onClick={() => handleSelect(category.type)}
									isActive={activeCategoryIndex === index}
									key={`category#${category.type}`}
									onMouseEnter={() => setActiveCategoryIndex(index)}
									onMouseLeave={() => setActiveCategoryIndex(null)}
									bgColor={
										category.type === TechTuningCategory.SETTINGS
											? '#2D70DA'
											: undefined
									}
								/>
							))}
						</ShopCategoriesGrid>
					</Fragment>
				)}
			</div>
			{!isInventory && (
				<StyledControlsContainer>
					<PanelControls controls={controls} align="left" />
				</StyledControlsContainer>
			)}
		</Fragment>
	);
};

export { CategoryTab };
